/**
 * Klientside-kryptografi for Passord-modulen (nullkunnskap).
 * Bruker kun Web Crypto API. Serveren ser aldri hovedpassord, hvelvnøkkel eller klartekst.
 */

const enc = new TextEncoder();
const dec = new TextDecoder();
const subtle = window.crypto.subtle;

export const PBKDF2_ITERATIONS = 600000;
export const AUTH_CONTEXT = "control-vault-auth";

export const b64 = (buf) => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
};
export const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
export const randomBytes = (n) => window.crypto.getRandomValues(new Uint8Array(n));

// ---------------------------------------------------------------------------
// Nøkkelutledning og wrapping
// ---------------------------------------------------------------------------

/** Utleder KEK (AES-256-GCM) fra passord/kode + salt med PBKDF2-SHA256. */
export async function deriveKek(secret, saltB64, iterations = PBKDF2_ITERATIONS) {
  const base = await subtle.importKey("raw", enc.encode(secret.normalize("NFKC")), "PBKDF2", false, ["deriveKey"]);
  return subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt: unb64(saltB64), iterations }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

/** Genererer en ny hvelvnøkkel (VK) som rå bytes. */
export const generateVaultKeyBytes = () => randomBytes(32);

export async function importAesKey(rawBytes, extractable = false) {
  return subtle.importKey("raw", rawBytes, { name: "AES-GCM", length: 256 }, extractable, ["encrypt", "decrypt"]);
}

export async function aesEncrypt(key, dataBytes) {
  const iv = randomBytes(12);
  const ct = await subtle.encrypt({ name: "AES-GCM", iv }, key, dataBytes);
  return { iv: b64(iv), ct: b64(ct) };
}

export async function aesDecrypt(key, cipher) {
  const pt = await subtle.decrypt({ name: "AES-GCM", iv: unb64(cipher.iv) }, key, unb64(cipher.ct));
  return new Uint8Array(pt);
}

export async function encryptJson(key, obj) {
  return aesEncrypt(key, enc.encode(JSON.stringify(obj)));
}

export async function decryptJson(key, cipher) {
  return JSON.parse(dec.decode(await aesDecrypt(key, cipher)));
}

/** Autentiseringstoken som beviser at klienten kjenner VK (serveren lagrer kun SHA-256 av denne). */
export async function authTokenFromVk(vkBytes) {
  const hk = await subtle.importKey("raw", vkBytes, { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return b64(await subtle.sign("HMAC", hk, enc.encode(AUTH_CONTEXT)));
}

// ---------------------------------------------------------------------------
// RSA-OAEP for deling
// ---------------------------------------------------------------------------

export async function generateKeyPair() {
  const kp = await subtle.generateKey({ name: "RSA-OAEP", modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" }, true, ["encrypt", "decrypt", "wrapKey", "unwrapKey"]);
  const spki = await subtle.exportKey("spki", kp.publicKey);
  const pkcs8 = await subtle.exportKey("pkcs8", kp.privateKey);
  return { publicKeyB64: b64(spki), privateKeyBytes: new Uint8Array(pkcs8) };
}

export async function importPublicKey(spkiB64) {
  return subtle.importKey("spki", unb64(spkiB64), { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
}

export async function importPrivateKey(pkcs8Bytes) {
  return subtle.importKey("pkcs8", pkcs8Bytes, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["decrypt"]);
}

/** Krypterer en oppføring for en mottaker: tilfeldig entry-nøkkel wrappes med mottakers public key. */
export async function encryptForRecipient(publicKeyB64, obj) {
  const ekBytes = randomBytes(32);
  const ek = await importAesKey(ekBytes);
  const blob = await encryptJson(ek, obj);
  const pub = await importPublicKey(publicKeyB64);
  const wrapped = await subtle.encrypt({ name: "RSA-OAEP" }, pub, ekBytes);
  return { wrapped_key: b64(wrapped), blob };
}

export async function decryptShared(privateKey, share) {
  const ekBytes = await subtle.decrypt({ name: "RSA-OAEP" }, privateKey, unb64(share.wrapped_key));
  const ek = await importAesKey(new Uint8Array(ekBytes));
  return decryptJson(ek, share.blob);
}

// ---------------------------------------------------------------------------
// Gjenopprettingskode
// ---------------------------------------------------------------------------

const RECOVERY_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // uten 0/O/1/I

export function generateRecoveryCode(groups = 6) {
  const bytes = randomBytes(groups * 4);
  const chars = Array.from(bytes, (b) => RECOVERY_ALPHABET[b % RECOVERY_ALPHABET.length]);
  const parts = [];
  for (let i = 0; i < groups; i += 1) parts.push(chars.slice(i * 4, i * 4 + 4).join(""));
  return parts.join("-");
}

export const normalizeRecoveryCode = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/(.{4})(?=.)/g, "$1-");

// ---------------------------------------------------------------------------
// Hvelv-oppsett og opplåsing
// ---------------------------------------------------------------------------

/** Bygger komplett setup-payload for serveren. Returnerer også nøkler for umiddelbar bruk. */
export async function buildVaultSetup(masterPassword) {
  const vkBytes = generateVaultKeyBytes();
  const vk = await importAesKey(vkBytes);
  const salt = b64(randomBytes(16));
  const kek = await deriveKek(masterPassword, salt);
  const wrapped_vk = await aesEncrypt(kek, vkBytes);
  const { publicKeyB64, privateKeyBytes } = await generateKeyPair();
  const wrapped_private_key = await aesEncrypt(vk, privateKeyBytes);
  const recoveryCode = generateRecoveryCode();
  const recSalt = b64(randomBytes(16));
  const recKek = await deriveKek(recoveryCode, recSalt);
  const recovery = { kdf: { algo: "PBKDF2-SHA256", iterations: PBKDF2_ITERATIONS, salt: recSalt }, wrapped_vk: await aesEncrypt(recKek, vkBytes) };
  const auth_token = await authTokenFromVk(vkBytes);
  const privateKey = await importPrivateKey(privateKeyBytes);
  return {
    payload: { kdf: { algo: "PBKDF2-SHA256", iterations: PBKDF2_ITERATIONS, salt }, wrapped_vk, recovery, public_key: publicKeyB64, wrapped_private_key, auth_token },
    keys: { vk, vkBytes, privateKey, authToken: auth_token, publicKeyB64 },
    recoveryCode,
  };
}

/** Låser opp med hovedpassord (eller gjenopprettingskode når useRecovery=true). Kaster ved feil. */
export async function unlockVault(meta, secret, useRecovery = false) {
  const src = useRecovery ? meta.recovery : meta;
  if (!src) throw new Error("Ingen gjenopprettingskode er registrert");
  const kek = await deriveKek(useRecovery ? normalizeRecoveryCode(secret) : secret, src.kdf.salt, src.kdf.iterations);
  const vkBytes = await aesDecrypt(kek, src.wrapped_vk);
  const vk = await importAesKey(vkBytes);
  const privateKeyBytes = await aesDecrypt(vk, meta.wrapped_private_key);
  const privateKey = await importPrivateKey(privateKeyBytes);
  const authToken = await authTokenFromVk(vkBytes);
  return { vk, vkBytes, privateKey, authToken, publicKeyB64: meta.public_key };
}

/** Lager ny wrapper for VK med nytt hovedpassord. */
export async function rewrapWithPassword(vkBytes, newPassword) {
  const salt = b64(randomBytes(16));
  const kek = await deriveKek(newPassword, salt);
  return { kdf: { algo: "PBKDF2-SHA256", iterations: PBKDF2_ITERATIONS, salt }, wrapped_vk: await aesEncrypt(kek, vkBytes) };
}

export async function newRecoveryWrap(vkBytes) {
  const recoveryCode = generateRecoveryCode();
  const salt = b64(randomBytes(16));
  const kek = await deriveKek(recoveryCode, salt);
  return { recoveryCode, recovery: { kdf: { algo: "PBKDF2-SHA256", iterations: PBKDF2_ITERATIONS, salt }, wrapped_vk: await aesEncrypt(kek, vkBytes) } };
}

// ---------------------------------------------------------------------------
// Passordgenerator og styrke
// ---------------------------------------------------------------------------

const SETS = { lower: "abcdefghijklmnopqrstuvwxyz", upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ", digits: "0123456789", symbols: "!@#$%&*+-=?^_~" };
const AMBIGUOUS = /[Il1O0]/g;

export function generatePassword({ length = 20, upper = true, digits = true, symbols = true, avoidAmbiguous = false } = {}) {
  let pool = SETS.lower + (upper ? SETS.upper : "") + (digits ? SETS.digits : "") + (symbols ? SETS.symbols : "");
  if (avoidAmbiguous) pool = pool.replace(AMBIGUOUS, "");
  const required = [SETS.lower, upper && SETS.upper, digits && SETS.digits, symbols && SETS.symbols].filter(Boolean).map((s) => (avoidAmbiguous ? s.replace(AMBIGUOUS, "") : s));
  const pick = (chars) => chars[randomBytes(1)[0] % chars.length];
  const out = required.map(pick);
  while (out.length < length) out.push(pick(pool));
  // Fisher–Yates med krypto-tilfeldighet
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = randomBytes(1)[0] % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.slice(0, length).join("");
}

/** Enkel entropi-basert styrkevurdering. Returnerer {score 0-4, label, bits}. */
export function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "Tomt", bits: 0 };
  let pool = 0;
  if (/[a-zæøå]/.test(pw)) pool += 26;
  if (/[A-ZÆØÅ]/.test(pw)) pool += 26;
  if (/\d/.test(pw)) pool += 10;
  if (/[^A-Za-z0-9æøåÆØÅ]/.test(pw)) pool += 20;
  if (/\s/.test(pw)) pool += 5;
  let bits = pw.length * Math.log2(Math.max(pool, 2));
  // Straff for enkle mønstre
  if (/^(.)\1+$/.test(pw)) bits *= 0.2;
  if (/(012|123|234|345|456|567|678|789|abc|qwe|asd|zxc|passord|password)/i.test(pw)) bits *= 0.6;
  if (/^[a-z]+$/i.test(pw) && pw.length < 10) bits *= 0.7;
  const words = pw.trim().split(/\s+/).length;
  if (words >= 4 && pw.length >= 16) bits = Math.max(bits, 60);
  let score = 0;
  if (bits >= 28) score = 1;
  if (bits >= 45) score = 2;
  if (bits >= 65) score = 3;
  if (bits >= 90) score = 4;
  const labels = ["Veldig svakt", "Svakt", "Middels", "Sterkt", "Veldig sterkt"];
  return { score, label: labels[score], bits: Math.round(bits) };
}

// ---------------------------------------------------------------------------
// TOTP (RFC 6238)
// ---------------------------------------------------------------------------

const B32 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

export function base32Decode(input) {
  const clean = (input || "").toUpperCase().replace(/[^A-Z2-7]/g, "");
  let bits = "";
  for (const ch of clean) bits += B32.indexOf(ch).toString(2).padStart(5, "0");
  const bytes = [];
  for (let i = 0; i + 8 <= bits.length; i += 8) bytes.push(parseInt(bits.slice(i, i + 8), 2));
  return new Uint8Array(bytes);
}

/** Tolker enten rå base32-secret eller otpauth://totp/...?secret=...&digits=&period= */
export function parseTotpInput(input) {
  const s = (input || "").trim();
  if (!s) return null;
  if (s.toLowerCase().startsWith("otpauth://")) {
    try {
      const u = new URL(s);
      const secret = u.searchParams.get("secret") || "";
      return { secret: secret.toUpperCase().replace(/[^A-Z2-7]/g, ""), digits: Number(u.searchParams.get("digits")) || 6, period: Number(u.searchParams.get("period")) || 30, algorithm: (u.searchParams.get("algorithm") || "SHA1").toUpperCase() };
    } catch {
      return null;
    }
  }
  const secret = s.toUpperCase().replace(/[^A-Z2-7]/g, "");
  return secret.length >= 16 ? { secret, digits: 6, period: 30, algorithm: "SHA1" } : null;
}

export async function totpCode(totp, now = Date.now()) {
  const { secret, digits = 6, period = 30, algorithm = "SHA1" } = totp;
  const key = await subtle.importKey("raw", base32Decode(secret), { name: "HMAC", hash: { name: algorithm === "SHA256" ? "SHA-256" : algorithm === "SHA512" ? "SHA-512" : "SHA-1" } }, false, ["sign"]);
  const counter = Math.floor(now / 1000 / period);
  const msg = new Uint8Array(8);
  let c = counter;
  for (let i = 7; i >= 0; i -= 1) { msg[i] = c & 0xff; c = Math.floor(c / 256); }
  const mac = new Uint8Array(await subtle.sign("HMAC", key, msg));
  const offset = mac[mac.length - 1] & 0x0f;
  const bin = ((mac[offset] & 0x7f) << 24) | (mac[offset + 1] << 16) | (mac[offset + 2] << 8) | mac[offset + 3];
  return String(bin % 10 ** digits).padStart(digits, "0");
}

export const totpRemaining = (period = 30, now = Date.now()) => period - (Math.floor(now / 1000) % period);

// ---------------------------------------------------------------------------
// Utklippstavle
// ---------------------------------------------------------------------------

let clearTimer = null;
export async function copySensitive(text, clearAfterSeconds = 30) {
  await navigator.clipboard.writeText(text);
  if (clearTimer) clearTimeout(clearTimer);
  if (clearAfterSeconds > 0) {
    clearTimer = setTimeout(async () => {
      try {
        await navigator.clipboard.writeText("");
      } catch {
        /* ignorer – vinduet kan ha mistet fokus */
      }
    }, clearAfterSeconds * 1000);
  }
}

export async function copyPlain(text) {
  await navigator.clipboard.writeText(text);
}

/** Normaliserer en URL for «Gå til» (legger på https:// om det mangler). */
export function normalizeUrl(url) {
  const s = (url || "").trim();
  if (!s) return "";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) return s;
  return `https://${s}`;
}

export function hostnameOf(url) {
  try {
    return new URL(normalizeUrl(url)).hostname.replace(/^www\./, "");
  } catch {
    return url || "";
  }
}
